import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PermissionsDrawer from "./PermissionsDrawer";
import { rbacService } from "../../../services/rbac.service";

const PINK = "#d86a78";
const PINK_HOVER = "#c95b69";
const BORDER = "#f2c6cc";
const RING = "#f7d7db";

function Input({ label, required, placeholder = "", value, onChange }) {
  return (
    <div className="w-full">
      <label className="mb-1.5 block text-[12px] text-slate-500">
        {label}
        {required ? <span style={{ color: PINK }}> *</span> : null}
      </label>
      <input
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="h-[42px] w-full rounded-[10px] bg-white px-4 text-[13px] text-slate-700 outline-none"
        style={{ border: `1px solid ${BORDER}` }}
        onFocus={(e) => {
          e.currentTarget.style.boxShadow = `0 0 0 4px ${RING}`;
          e.currentTarget.style.borderColor = PINK;
        }}
        onBlur={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.borderColor = BORDER;
        }}
      />
    </div>
  );
}

function Textarea({ label, value, onChange, placeholder = "" }) {
  return (
    <div className="w-full">
      <label className="mb-1.5 block text-[12px] text-slate-500">{label}</label>
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={4}
        className="w-full rounded-[10px] bg-white px-4 py-3 text-[13px] text-slate-700 outline-none resize-none"
        style={{ border: `1px solid ${BORDER}` }}
        onFocus={(e) => {
          e.currentTarget.style.boxShadow = `0 0 0 4px ${RING}`;
          e.currentTarget.style.borderColor = PINK;
        }}
        onBlur={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.borderColor = BORDER;
        }}
      />
    </div>
  );
}

function Select({ label, required, value, onChange, options }) {
  return (
    <div className="w-full">
      <label className="mb-1.5 block text-[12px] text-slate-500">
        {label}
        {required ? <span style={{ color: PINK }}> *</span> : null}
      </label>
      <select
        value={value}
        onChange={onChange}
        className="h-[42px] w-full rounded-[10px] bg-white px-4 text-[13px] text-slate-700 outline-none"
        style={{ border: `1px solid ${BORDER}` }}
        onFocus={(e) => {
          e.currentTarget.style.boxShadow = `0 0 0 4px ${RING}`;
          e.currentTarget.style.borderColor = PINK;
        }}
        onBlur={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.borderColor = BORDER;
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function PillButton({ children, onClick, muted = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-[40px] items-center justify-center rounded-full px-5 text-[13px] font-semibold transition"
      style={{
        background: muted ? "#fff" : PINK,
        border: `1px solid ${muted ? BORDER : PINK}`,
        color: muted ? "#64748b" : "#fff",
      }}
    >
      {children}
    </button>
  );
}

function appliesToLabel(value) {
  if (value === "admin_employee") return "Admin Employee";
  if (value === "warehouse_employee") return "Warehouse Employee";
  return "Both";
}

export default function AddNewRole() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [permOpen, setPermOpen] = useState(false);
  const [permissionIds, setPermissionIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingRole, setLoadingRole] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    roleName: "",
    appliesTo: "admin_employee",
    description: "",
    code: "",
    priority: "medium",
    status: "active",
  });

  useEffect(() => {
    if (!isEditMode) return;
    let mounted = true;

    (async () => {
      try {
        setLoadingRole(true);
        setError("");
        const role = await rbacService.getRole(id);
        if (!mounted) return;

        const descriptionParts = String(role?.description || "").split(" | ");
        const cleanDescription = descriptionParts
          .filter((part) => !String(part).startsWith("Code:") && !String(part).startsWith("Priority:") && !String(part).startsWith("Status:"))
          .join(" | ");
        const codePart = descriptionParts.find((part) => String(part).startsWith("Code:"));
        const priorityPart = descriptionParts.find((part) => String(part).startsWith("Priority:"));
        const statusPart = descriptionParts.find((part) => String(part).startsWith("Status:"));

        setForm({
          roleName: role?.name || "",
          appliesTo: role?.appliesTo || "admin_employee",
          description: cleanDescription || "",
          code: codePart ? String(codePart).replace("Code:", "").trim() : "",
          priority: priorityPart ? String(priorityPart).replace("Priority:", "").trim() : "medium",
          status: statusPart ? String(statusPart).replace("Status:", "").trim() : "active",
        });

        setPermissionIds(Array.isArray(role?.permissionIds) ? role.permissionIds : []);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "Failed to load role");
      } finally {
        if (mounted) setLoadingRole(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [id, isEditMode]);

  const onChange = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));

    if (key === "appliesTo") {
      setPermissionIds([]);
    }
  };

  const selectedTargetHelp = useMemo(() => {
    if (form.appliesTo === "admin_employee") {
      return "Warehouse permissions will stay hidden. Only admin panel permissions can be assigned.";
    }
    if (form.appliesTo === "warehouse_employee") {
      return "Admin panel permissions will stay hidden. Only warehouse permissions can be assigned.";
    }
    return "Shared permissions can be assigned to this role.";
  }, [form.appliesTo]);

  const onCancel = () => {
    if (saving) return;
    if (isEditMode) {
      navigate("/admin/dashboard/roles");
      return;
    }
    setError("");
    setPermissionIds([]);
    setForm({
      roleName: "",
      appliesTo: "admin_employee",
      description: "",
      code: "",
      priority: "medium",
      status: "active",
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError("");

      const name = String(form.roleName || "").trim();
      if (!name) {
        setError("Role name is required");
        return;
      }

      const payload = {
        name,
        appliesTo: form.appliesTo,
        description: [String(form.description || "").trim(), form.code ? `Code: ${String(form.code).trim()}` : "", form.priority ? `Priority: ${form.priority}` : "", form.status ? `Status: ${form.status}` : ""]
          .filter(Boolean)
          .join(" | "),
        permissionIds,
      };

      if (isEditMode) await rbacService.updateRole(id, payload);
      else await rbacService.createRole(payload);

      navigate("/admin/dashboard/roles");
    } catch (e2) {
      setError(e2?.message || (isEditMode ? "Failed to update role" : "Failed to create role"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-white">
      <PermissionsDrawer
        open={permOpen}
        value={permissionIds}
        onClose={() => setPermOpen(false)}
        onSave={(ids) => setPermissionIds(ids)}
        target={form.appliesTo}
      />

      <div className="mx-auto w-full max-w-[1080px] px-4 py-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-[25px] font-bold tracking-wide text-left" style={{ color: PINK }}>
              {isEditMode ? "EDIT ROLE" : "ADD NEW ROLE"}
            </h1>
            <p className="mt-1 text-[12px] text-slate-500">
              {isEditMode
                ? "Update role details and mapped permissions."
                : "Create a new role, choose who it is for, and assign only the relevant permissions."}
            </p>
          </div>

          {isEditMode ? (
            <button
              type="button"
              onClick={() => navigate("/admin/dashboard/roles")}
              disabled={saving}
              className="inline-flex h-[38px] items-center justify-center gap-2 rounded-full border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-slate-600 transition hover:border-[#d86a78] hover:bg-[#fff7f8] hover:text-[#d86a78] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ArrowLeft size={15} />
              Back to Roles
            </button>
          ) : null}
        </div>

        <form onSubmit={onSubmit} className="mt-6 rounded-[18px] border border-slate-200/70 bg-white p-6 shadow-sm">
          {loadingRole ? (
            <div className="mb-5 rounded-[10px] border border-[#f2c6cc] bg-[#fffafb] px-4 py-3 text-[12px] text-slate-500">
              Loading role details...
            </div>
          ) : null}

          {error ? (
            <div className="mb-5 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-600">
              {error}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Input
              label="Name of the Role"
              required
              placeholder="Enter role name"
              value={form.roleName}
              onChange={onChange("roleName")}
            />

            <Select
              label="Role For"
              required
              value={form.appliesTo}
              onChange={onChange("appliesTo")}
              options={[
                { value: "admin_employee", label: "Admin Employee" },
                { value: "warehouse_employee", label: "Warehouse Employee" },
                { value: "both", label: "Both" },
              ]}
            />

            <Input
              label="Role Code"
              placeholder="Ex: ADM_OPS_MANAGER"
              value={form.code}
              onChange={onChange("code")}
            />

            <Select
              label="Priority"
              value={form.priority}
              onChange={onChange("priority")}
              options={[
                { value: "low", label: "Low" },
                { value: "medium", label: "Medium" },
                { value: "high", label: "High" },
              ]}
            />

            <Select
              label="Status"
              value={form.status}
              onChange={onChange("status")}
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
            />
          </div>

          <div className="mt-6">
            <Textarea
              label="Description"
              placeholder="Add short description for this role"
              value={form.description}
              onChange={onChange("description")}
            />
          </div>

          <div className="relative my-8">
            <div className="h-px w-full" style={{ background: BORDER }} />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4">
              <span className="text-[11px] font-semibold" style={{ color: PINK }}>
                PERMISSIONS FOR THIS ROLE
              </span>
            </div>
          </div>

          <div className="rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-[14px] font-semibold text-slate-700">
                  Selected Target: {appliesToLabel(form.appliesTo)}
                </div>
                <div className="mt-1 text-[12px] text-slate-500">{selectedTargetHelp}</div>
              </div>

              <div className="flex flex-wrap gap-3">
                <PillButton onClick={() => setPermOpen(true)}>Assign Permissions</PillButton>
                <PillButton muted onClick={() => setPermissionIds([])}>
                  Clear Selection
                </PillButton>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {permissionIds.length ? (
                <span className="rounded-full border border-[#f2c6cc] bg-white px-3 py-1 text-[12px] font-semibold text-[#d86a78]">
                  {permissionIds.length} permissions selected
                </span>
              ) : (
                <span className="text-[12px] text-slate-400">No permissions selected yet.</span>
              )}
            </div>
          </div>

          <div className="mt-10 flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving || loadingRole}
              className="h-[36px] w-[160px] rounded-[8px] text-[12px] font-semibold text-white"
              style={{ background: "#6B7280" }}
            >
              {isEditMode ? "BACK" : "RESET"}
            </button>

            <button
              type="submit"
              disabled={saving || loadingRole}
              className="h-[36px] w-[160px] rounded-[8px] text-[12px] font-semibold text-white transition"
              style={{ background: PINK }}
              onMouseEnter={(e) => {
                if (!saving) e.currentTarget.style.background = PINK_HOVER;
              }}
              onMouseLeave={(e) => {
                if (!saving) e.currentTarget.style.background = PINK;
              }}
            >
              {saving ? (isEditMode ? "UPDATING..." : "CREATING...") : isEditMode ? "UPDATE ROLE" : "CREATE ROLE"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
